/** Non-streaming API calls: history, feedback, drafting, and the back office. */

import type {
  DocumentPassages,
  AnalyticsOverview,
  DeepHealth,
  AuditSearchResult,
  CorrectionTask,
  DraftRequest,
  DraftResponse,
  FeatureDraft,
  FeatureOffer,
  SpeechStatus,
  Transcript,
  IngestionJob,
  IngestionJobList,
  TextDocumentInput,
  KbDocument,
  KbDocumentList,
  RetrievalPreview,
  SessionMessage,
  SessionSummary,
  SupervisionFlag,
  SupervisionQueue,
} from "./types";

export interface ApiOptions {
  baseUrl?: string;
  employeeId?: string;
  /** Development-mode role. Ignored by the server outside development. */
  role?: string;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly status: number;
  /**
   * The parsed error body, when the server sent one.
   *
   * Carried rather than discarded because several endpoints refuse *meaningfully*: a drafting
   * request with no grounding comes back as 422 with the department to contact, and a UI that
   * showed only "request failed" would throw away the one thing the employee needs next.
   */
  readonly detail: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

function headersFor(options: ApiOptions, extra: Record<string, string> = {}) {
  const headers: Record<string, string> = { Accept: "application/json", ...extra };
  // Development identity. In a deployed environment the SSO session cookie carries it and this
  // header is ignored by the server, so sending it costs nothing and simplifies local work.
  if (options.employeeId) headers["X-NAB-Employee-Id"] = options.employeeId;
  if (options.role) headers["X-NAB-Role"] = options.role;
  return headers;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  return (await response.json()) as T;
}

async function get<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const response = await fetch(`${options.baseUrl ?? "/api"}${path}`, {
    headers: headersFor(options),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<T>(response);
}

async function post<T>(path: string, body: unknown, options: ApiOptions = {}): Promise<T> {
  const response = await fetch(`${options.baseUrl ?? "/api"}${path}`, {
    method: "POST",
    headers: headersFor(options, { "Content-Type": "application/json" }),
    body: JSON.stringify(body),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<T>(response);
}

/** The employee's own sessions, most recently active first. */
export async function fetchSessions(options?: ApiOptions): Promise<SessionSummary[]> {
  const body = await get<{ sessions: SessionSummary[] }>("/sessions", options);
  return body.sessions;
}

/** Messages in one of the employee's own sessions. 404 for anyone else's. */
export async function fetchSessionMessages(
  sessionId: string,
  options?: ApiOptions,
): Promise<SessionMessage[]> {
  const body = await get<{ messages: SessionMessage[] }>(
    `/sessions/${encodeURIComponent(sessionId)}/messages`,
    options,
  );
  return body.messages;
}

// ---------------------------------------------------------------------------------------
// UC-A5 — feedback
// ---------------------------------------------------------------------------------------

export interface FeedbackResult {
  feedback_id: string;
  rating: "up" | "down";
  /** Whether a supervision flag opened. The UI promises this to the employee, so it is read
   *  from the server's answer rather than assumed from the rating. */
  sent_to_review: boolean;
}

export async function submitFeedback(
  input: { message_id: string; rating: "up" | "down"; comment?: string },
  options?: ApiOptions,
): Promise<FeedbackResult> {
  return post<FeedbackResult>("/feedback", input, options);
}

// ---------------------------------------------------------------------------------------
// UC-A4 — drafting
// ---------------------------------------------------------------------------------------

export async function requestDraft(
  input: DraftRequest,
  options?: ApiOptions,
): Promise<DraftResponse> {
  return post<DraftResponse>("/drafts", input, options);
}

// ---------------------------------------------------------------------------------------
// UC-B1, UC-B2, UC-B3 — back office
// ---------------------------------------------------------------------------------------

export async function fetchKbDocuments(
  params: {
    corpus?: string;
    status?: string;
    /** Matches the title or document id, case-insensitively. Server-side: see `list_documents`. */
    q?: string;
    limit?: number;
    offset?: number;
  } = {},
  options?: ApiOptions,
): Promise<KbDocumentList> {
  return get<KbDocumentList>(`/admin/kb/documents${query(params)}`, options);
}

/**
 * Queue a file for indexing.
 *
 * Returns a *job*, not a document: ingestion runs in the background (Phase 3), so the server
 * answers 202 and the client polls `fetchIngestionJobs` until the queue drains. See
 * docs/adr/0005-asynchronous-ingestion.md.
 */
export async function uploadKbDocument(
  file: File,
  metadata: Record<string, string>,
  options: ApiOptions = {},
): Promise<IngestionJob> {
  const form = new FormData();
  form.append("file", file);
  for (const [key, value] of Object.entries(metadata)) {
    if (value) form.append(key, value);
  }
  // No Content-Type header: the browser sets it with the multipart boundary, and overriding it
  // produces a body the server cannot parse.
  const response = await fetch(`${options.baseUrl ?? "/api"}/admin/kb/documents`, {
    method: "POST",
    headers: headersFor(options),
    body: form,
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<IngestionJob>(response);
}

/** Index a document typed or pasted into the back office. Same pipeline, same citations. */
export async function createTextDocument(
  input: TextDocumentInput,
  options?: ApiOptions,
): Promise<IngestionJob> {
  return post<IngestionJob>("/admin/kb/documents/text", input, options);
}

/** The ingestion queue, newest first. Polled while `active` is above zero. */
export async function fetchIngestionJobs(
  params: { status?: string } = {},
  options?: ApiOptions,
): Promise<IngestionJobList> {
  return get<IngestionJobList>(`/admin/kb/jobs${query(params)}`, options);
}

/** Re-run a failed job against its staged bytes, so a retry is not a re-upload. */
export async function retryIngestionJob(
  jobId: string,
  options?: ApiOptions,
): Promise<IngestionJob> {
  return post<IngestionJob>(
    `/admin/kb/jobs/${encodeURIComponent(jobId)}/retry`,
    {},
    options,
  );
}

export async function retireKbDocument(
  docId: string,
  version: string,
  options?: ApiOptions,
): Promise<KbDocument> {
  return post<KbDocument>(
    `/admin/kb/documents/${encodeURIComponent(docId)}/retire?version=${encodeURIComponent(version)}`,
    {},
    options,
  );
}

/**
 * Take a document off the knowledge base.
 *
 * Different from retiring, which is why both exist. Retiring says "superseded" and the version
 * stays on the list; deleting says "this should not be here" and it leaves the list. Neither
 * removes the row — an audit event records the chunk ids behind every answer, and resolving them
 * years later is what keeps a 2026 answer explainable.
 *
 * `purge` additionally removes the passages from the vector store, which is the one action that
 * breaks that resolution. It is for contents that should not sit in a vector store at all.
 */
export async function deleteKbDocument(
  docId: string,
  version: string,
  options?: ApiOptions & { purge?: boolean },
): Promise<KbDocument> {
  const purge = options?.purge ? "&purge=true" : "";
  const path = `/admin/kb/documents/${encodeURIComponent(docId)}?version=${encodeURIComponent(
    version,
  )}${purge}`;
  const response = await fetch(`${options?.baseUrl ?? "/api"}${path}`, {
    method: "DELETE",
    headers: headersFor(options ?? {}),
    credentials: "same-origin",
    signal: options?.signal ?? null,
  });
  return unwrap<KbDocument>(response);
}

/**
 * A document as the assistant holds it: the passages it retrieves from, in document order.
 *
 * Not the original file. What the assistant answers from is the chunks, and every ingestion defect
 * lives in the gap between the two — a fee table flattened into a sentence, an OCR page that came
 * back as noise, a heading lost so a citation has nowhere to point.
 */
export async function fetchDocumentPassages(
  docId: string,
  options?: ApiOptions,
): Promise<DocumentPassages> {
  return get<DocumentPassages>(
    `/admin/kb/documents/${encodeURIComponent(docId)}/passages`,
    options,
  );
}

export async function previewRetrieval(
  input: { query: string; include_retired?: boolean; top_k?: number },
  options?: ApiOptions,
): Promise<RetrievalPreview> {
  return post<RetrievalPreview>("/admin/kb/preview", input, options);
}

export async function fetchSupervisionQueue(
  params: {
    status?: string;
    source?: string;
    language?: string;
    limit?: number;
    offset?: number;
  } = {},
  options?: ApiOptions,
): Promise<SupervisionQueue> {
  return get<SupervisionQueue>(`/admin/supervision${query(params)}`, options);
}

export async function annotateFlag(
  flagId: string,
  annotation: string,
  options?: ApiOptions,
): Promise<SupervisionFlag> {
  return post<SupervisionFlag>(
    `/admin/supervision/${encodeURIComponent(flagId)}/annotate`,
    { annotation },
    options,
  );
}

export async function dismissFlag(
  flagId: string,
  options?: ApiOptions,
): Promise<SupervisionFlag> {
  return post<SupervisionFlag>(
    `/admin/supervision/${encodeURIComponent(flagId)}/dismiss`,
    {},
    options,
  );
}

export async function createCorrectionTask(
  flagId: string,
  input: { title: string; description?: string; doc_id?: string | null },
  options?: ApiOptions,
): Promise<CorrectionTask> {
  return post<CorrectionTask>(
    `/admin/supervision/${encodeURIComponent(flagId)}/correction-task`,
    input,
    options,
  );
}

export async function fetchCorrectionTasks(
  params: { status?: string } = {},
  options?: ApiOptions,
): Promise<CorrectionTask[]> {
  return get<CorrectionTask[]>(`/admin/correction-tasks${query(params)}`, options);
}

export async function fetchAnalytics(
  days = 30,
  options?: ApiOptions,
): Promise<AnalyticsOverview> {
  return get<AnalyticsOverview>(`/admin/analytics?days=${days}`, options);
}

export async function searchAudit(
  params: Record<string, string | number | undefined>,
  options?: ApiOptions,
): Promise<AuditSearchResult> {
  return get<AuditSearchResult>(`/admin/audit/search${query(params)}`, options);
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/**
 * Deep health: generation, retrieval, index contents and store consistency.
 *
 * Expensive by design — it generates a token and runs a real query — so it is fetched on demand
 * from the back office rather than polled.
 */
export async function fetchDeepHealth(options?: ApiOptions): Promise<DeepHealth> {
  return get<DeepHealth>("/health/deep", options);
}

// ---------------------------------------------------------------------------------------
// Features (UC-B4)
//
// What the assistant can *do*, as an administrator defines it. See
// docs/adr/0007-admin-defined-actions.md — in particular, why there is no field here that takes a
// URL: a feature names a connector and an action by key, both from what an engineer has onboarded.
// ---------------------------------------------------------------------------------------

export interface LocalizedText {
  en: string;
  ar: string;
}

export interface FeatureFieldSpec {
  key: string;
  type: "text" | "long_text" | "date" | "number" | "choice" | "boolean";
  label: LocalizedText;
  help_text?: LocalizedText | null;
  required: boolean;
  options: [string, LocalizedText][];
}

export interface FeatureRuleSpec {
  type: string;
  field: string;
  other_field: string;
  value: number | null;
  message: LocalizedText;
}

export interface FeatureSubmissionSpec {
  connector: string;
  action: string;
  payload: Record<string, string>;
}

export interface FeatureDefinition {
  key: string;
  name: LocalizedText;
  description: LocalizedText;
  intent_examples: string[];
  intent_counter_examples: string[];
  fields: FeatureFieldSpec[];
  rules: FeatureRuleSpec[];
  submission: FeatureSubmissionSpec | null;
  roles: string[];
  risk_tier: "low" | "medium" | "high";
  status: "draft" | "active" | "retired";
}

export interface FeatureSummary {
  feature_id: string;
  key: string;
  status: string;
  name_en: string;
  name_ar: string;
  risk_tier: string;
  /** Whether it submits anywhere, or only drafts. The distinction that matters most at a glance. */
  submits: boolean;
  updated_by: string;
  updated_at: string;
}

export interface FeatureRead {
  feature_id: string;
  definition: FeatureDefinition;
  created_by: string;
  updated_by: string;
  updated_at: string;
}

export interface ConnectorAction {
  key: string;
  label_en: string;
  label_ar: string;
  method: string;
  /** Payload keys a feature may map to. The editor lists these; it never accepts free text. */
  accepts: string[];
  read_only: boolean;
}

export interface Connector {
  key: string;
  label_en: string;
  label_ar: string;
  description: string;
  enabled: boolean;
  /** Enabled *and* configured here. A feature cannot be activated against one that is not. */
  usable: boolean;
  actions: ConnectorAction[];
}

export interface ConnectorCatalogue {
  connectors: Connector[];
  /** Payload keys no feature may map. Shown in the editor before a save is attempted. */
  reserved_fields: string[];
}

export async function fetchFeatures(
  params: { status?: string } = {},
  options?: ApiOptions,
): Promise<{ features: FeatureSummary[] }> {
  return get<{ features: FeatureSummary[] }>(`/admin/features${query(params)}`, options);
}

export async function fetchFeature(
  featureId: string,
  options?: ApiOptions,
): Promise<FeatureRead> {
  return get<FeatureRead>(`/admin/features/${featureId}`, options);
}

/** What an engineer has onboarded. The editor's connector and action pickers come from this. */
export async function fetchConnectors(options?: ApiOptions): Promise<ConnectorCatalogue> {
  return get<ConnectorCatalogue>("/admin/features/connectors", options);
}

export async function createFeature(
  definition: FeatureDefinition,
  options?: ApiOptions,
): Promise<FeatureRead> {
  return post<FeatureRead>("/admin/features", definition, options);
}

export async function updateFeature(
  featureId: string,
  definition: FeatureDefinition,
  options: ApiOptions = {},
): Promise<FeatureRead> {
  const response = await fetch(`${options.baseUrl ?? "/api"}/admin/features/${featureId}`, {
    method: "PUT",
    headers: headersFor(options, { "Content-Type": "application/json" }),
    body: JSON.stringify(definition),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<FeatureRead>(response);
}

/**
 * Stop offering a feature, without deleting it.
 *
 * There is no delete. An employee's submitted request references the feature it was built from,
 * and deleting the definition would leave that record pointing at nothing — which is exactly the
 * state an auditor cannot work with.
 */
export async function retireFeature(
  featureId: string,
  options?: ApiOptions,
): Promise<FeatureRead> {
  return post<FeatureRead>(`/admin/features/${featureId}/retire`, {}, options);
}

// ---------------------------------------------------------------------------------------
// Using a feature (UC-B4)
//
// The employee's half. Starting a request, filling it in, and having it checked — see
// docs/adr/0007-admin-defined-actions.md §4: there is no submit call here, and there is not
// supposed to be. In M1.5 a draft ends at `ready` and the employee files it through the portal.
// ---------------------------------------------------------------------------------------

/** Active features this employee's role may use, in their language. */
export async function fetchMyFeatures(
  language: string,
  options?: ApiOptions,
): Promise<{ features: FeatureOffer[] }> {
  return get<{ features: FeatureOffer[] }>(`/features${query({ language })}`, options);
}

export async function startDraft(
  featureKey: string,
  body: { session_id?: string | null; language: string; values?: Record<string, unknown> },
  options?: ApiOptions,
): Promise<FeatureDraft> {
  return post<FeatureDraft>(`/features/${featureKey}/drafts`, body, options);
}

/**
 * Merge in what has been collected and have the whole thing re-checked.
 *
 * Answers 200 with problems rather than a 4xx. Half-filled is the normal state of a request being
 * collected a field at a time, and an error status would make the interface treat "three fields to
 * go" as a failure.
 */
export async function updateDraft(
  draftId: string,
  values: Record<string, unknown>,
  options: ApiOptions = {},
): Promise<FeatureDraft> {
  const response = await fetch(`${options.baseUrl ?? "/api"}/features/drafts/${draftId}`, {
    method: "PATCH",
    headers: headersFor(options, { "Content-Type": "application/json" }),
    body: JSON.stringify({ values }),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<FeatureDraft>(response);
}

export async function fetchDraft(
  draftId: string,
  options?: ApiOptions,
): Promise<FeatureDraft> {
  return get<FeatureDraft>(`/features/drafts/${draftId}`, options);
}

// ---------------------------------------------------------------------------------------
// Actions (UC-B4)
//
// What the assistant may *call*: the method, the path, the payload, what comes back. See
// docs/adr/0007-admin-defined-actions.md.
//
// Note what is absent from every type below: a host, a scheme, a credential, a TLS setting. Those
// live in configs/connectors.yaml and change only by deployment. The path is editable and
// *bounded* — `ActionTarget.allowed_path_prefixes` is what it must sit under, and the server
// refuses anything outside it with a message naming where writing is permitted.
// ---------------------------------------------------------------------------------------

export interface ActionPayloadFieldSpec {
  /** The name the far system expects. Theirs, not ours. */
  key: string;
  label?: LocalizedText | null;
  type: "text" | "date" | "number" | "boolean";
  /** `field` takes the employee's value; `constant` is fixed in the definition. */
  source: "field" | "constant";
  /** For `field` — the feature field this carries. Empty means the same name as `key`. */
  from_field: string;
  constant?: string | number | boolean | null;
  required: boolean;
}

export interface ActionResponseFieldSpec {
  key: string;
  /** Where to read it in the response body; a dotted path, so `data.request.id` works. */
  path: string;
  label?: LocalizedText | null;
}

export interface ActionDefinition {
  key: string;
  connector: string;
  label: LocalizedText;
  description: string;
  /** No DELETE: nothing typed into a web form should delete a record in a bank system. */
  method: "GET" | "POST" | "PUT" | "PATCH";
  path: string;
  payload: ActionPayloadFieldSpec[];
  returns: ActionResponseFieldSpec[];
  reference_key: string;
  status_key: string;
  read_only: boolean;
  status: "draft" | "active" | "retired";
}

export interface ActionSummary {
  action_id: string;
  connector: string;
  key: string;
  status: string;
  method: string;
  path: string;
  label_en: string;
  label_ar: string;
  read_only: boolean;
  /** How many keys this sends. The list's answer to "is this wired up yet". */
  payload_keys: number;
  updated_by: string;
  updated_at: string;
}

export interface ActionRead {
  action_id: string;
  definition: ActionDefinition;
  created_by: string;
  updated_by: string;
  updated_at: string;
}

/** A connector, as the action editor needs it: what bounds an action, never what reaches it. */
export interface ActionTarget {
  key: string;
  label_en: string;
  label_ar: string;
  description: string;
  enabled: boolean;
  usable: boolean;
  /** Where an action may be written. Empty means this connector accepts none at all. */
  allowed_path_prefixes: string[];
  /** Filled by the platform from the session, and refused in a payload. */
  identity_fields: string[];
  /** Declared in configs/connectors.yaml; a new action may not take one of these keys. */
  configured_action_keys: string[];
}

export interface ActionTargetList {
  connectors: ActionTarget[];
  reserved_fields: string[];
}

export async function fetchActions(
  params: { connector?: string; status?: string } = {},
  options?: ApiOptions,
): Promise<{ actions: ActionSummary[] }> {
  return get<{ actions: ActionSummary[] }>(`/admin/actions${query(params)}`, options);
}

export async function fetchAction(
  actionId: string,
  options?: ApiOptions,
): Promise<ActionRead> {
  return get<ActionRead>(`/admin/actions/${actionId}`, options);
}

/** The systems an action may be written against, and the paths each one permits. */
export async function fetchActionTargets(options?: ApiOptions): Promise<ActionTargetList> {
  return get<ActionTargetList>("/admin/actions/connectors", options);
}

export async function createAction(
  definition: ActionDefinition,
  options?: ApiOptions,
): Promise<ActionRead> {
  return post<ActionRead>("/admin/actions", definition, options);
}

export async function updateAction(
  actionId: string,
  definition: ActionDefinition,
  options: ApiOptions = {},
): Promise<ActionRead> {
  const response = await fetch(`${options.baseUrl ?? "/api"}/admin/actions/${actionId}`, {
    method: "PUT",
    headers: headersFor(options, { "Content-Type": "application/json" }),
    body: JSON.stringify(definition),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<ActionRead>(response);
}

/**
 * Stop features from using it, without deleting it.
 *
 * There is no delete, for the same reason there is none for features: a feature references an
 * action by key and a submitted request is a record of something that happened.
 */
export async function retireAction(
  actionId: string,
  options?: ApiOptions,
): Promise<ActionRead> {
  return post<ActionRead>(`/admin/actions/${actionId}/retire`, {}, options);
}

/**
 * Send a completed request (UC-B4).
 *
 * The only call in the client that causes an outbound request to a bank system, and it happens
 * because an employee pressed a button on their own draft. ADR 0007 §4.
 */
export async function submitDraft(
  draftId: string,
  options?: ApiOptions,
): Promise<FeatureDraft> {
  return post<FeatureDraft>(`/features/drafts/${draftId}/submit`, {}, options);
}

// ---------------------------------------------------------------------------------------
// Voice (UC-A6)
//
// Dictation in, read-aloud out. See docs/adr/0008-voice-input-and-read-aloud.md — in particular
// why the engine runs on NAB's own machine: an employee dictating a question says an account
// number out loud, and the browser's own SpeechRecognition streams that to Google in Chrome.
// ---------------------------------------------------------------------------------------

/** Whether voice is configured here. Read on load; the controls are absent when it is not. */
export async function fetchSpeechStatus(options?: ApiOptions): Promise<SpeechStatus> {
  return get<SpeechStatus>("/speech/status", options);
}

/**
 * Turn one recording into text.
 *
 * The audio is sent and never stored — not here, not on the server. What is recorded is the
 * transcript after masking, exactly as a typed message is.
 */
export async function transcribe(
  audio: Blob,
  params: { language?: string; sessionId?: string | null } = {},
  options: ApiOptions = {},
): Promise<Transcript> {
  const body = new FormData();
  // The filename is part of the multipart contract and names nothing on disk.
  body.append("file", audio, "recording");
  body.append("content_type", audio.type || "audio/webm");
  if (params.language) body.append("language", params.language);
  if (params.sessionId) body.append("session_id", params.sessionId);

  const response = await fetch(`${options.baseUrl ?? "/api"}/speech/transcribe`, {
    method: "POST",
    // No Content-Type: the browser sets it with the multipart boundary, and setting it by hand
    // produces a body the server cannot parse.
    headers: headersFor(options),
    body,
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  return unwrap<Transcript>(response);
}

/**
 * Read text aloud, returning the audio.
 *
 * `language` is the *answer's* language, not the interface's (golden rule 7): a question asked in
 * Arabic is read back in Arabic whatever the interface is set to.
 */
export async function speak(
  text: string,
  language: string,
  options: ApiOptions = {},
): Promise<Blob> {
  const response = await fetch(`${options.baseUrl ?? "/api"}/speech/speak`, {
    method: "POST",
    headers: headersFor(options, { "Content-Type": "application/json" }),
    body: JSON.stringify({ text, language }),
    credentials: "same-origin",
    signal: options.signal ?? null,
  });
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  return response.blob();
}
